import { isNull, max, min, noop, set } from 'lodash/fp';
import Slider from 'rc-slider';
import React, { useCallback, useMemo } from 'react';

import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { CustomTooltip } from '../../../shared/tooltip';
import styles from '../PortfolioAnalytics.module.scss';
import { PortfolioAnalyticsSurveyQuestion } from '../store';

const { frenchLight, grey, fixedWhite } = styles;

interface MarkerProps {
    portfolioQuestion: PortfolioAnalyticsSurveyQuestion;
}

const Marker: React.FC<MarkerProps> = ({ portfolioQuestion: { question, selectedOption } }) => {
    let answer = null;
    if (selectedOption) {
        answer = (selectedOption as DropdownOption).label;
    }
    const overlayText = `${question}${answer ? `: ${answer}` : ''}`;
    return (
        <CustomTooltip overlayText={overlayText} placement='top'>
            <div className={styles.sliderMarker} />
        </CustomTooltip>
    );
};

interface PortfolioAnalyticsSliderProps {
    surveyQuestionIndex: number;
    portfolioAnalyticsSurvey: PortfolioAnalyticsSurveyQuestion[];
    setQuestionIndex: (index: number) => void;
}

export const PortfolioAnalyticsSlider: React.FC<PortfolioAnalyticsSliderProps> = ({
    surveyQuestionIndex,
    portfolioAnalyticsSurvey,
    setQuestionIndex
}) => {

    const surveyQuestions = useMemo(() => portfolioAnalyticsSurvey.reduce((acc, portfolioQuestion, index) => set(index, <Marker portfolioQuestion={portfolioQuestion} />, acc), {}), [portfolioAnalyticsSurvey]);

    const minValue = min((Object.keys(surveyQuestions).map(val => parseInt(val))));
    const maxValue = max((Object.keys(surveyQuestions).map(val => parseInt(val))));
    const minimum = minValue && maxValue !== minValue ? minValue : 0;

    const selectSurveyPoint = useCallback((index: number | number[]) => {
        const indexNumber = index as number;
        if (indexNumber > surveyQuestionIndex && isNull(portfolioAnalyticsSurvey[surveyQuestionIndex].selectedOption)) {
            return noop;
        }
        if (!isNull(portfolioAnalyticsSurvey[indexNumber]) || isNull(portfolioAnalyticsSurvey[indexNumber]) && !isNull(portfolioAnalyticsSurvey[(indexNumber) - 1])) {
            setQuestionIndex(indexNumber);
        }
    }, [portfolioAnalyticsSurvey, setQuestionIndex, surveyQuestionIndex]);

    return (
        <div className={styles.surveyQuestionSlider}>
            <Slider
                min={minimum}
                marks={surveyQuestions}
                onChange={selectSurveyPoint}
                defaultValue={maxValue}
                value={surveyQuestionIndex}
                max={maxValue}
                step={1}
                trackStyle={{ backgroundColor: frenchLight }}
                railStyle={{ backgroundColor: grey }}
                handleStyle={{ backgroundColor: fixedWhite, border: `1px solid ${frenchLight}`, height: '16px', width: '16px', marginTop: '-6px', opacity: 1 }}
                dotStyle={{ backgroundColor: fixedWhite, border: `1px solid ${frenchLight}` }}
            />
        </div>
    );
};
